import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import colors from "../../../theme/colors";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import { PANEL_BTN_SX, PANEL_BTN_OUTLINE_SX } from "../panelButtonStyles";
import { submissionTypeLong } from "./tableChips";
import {
  submissionBorrowerName,
  submissionDocumentCount,
  submissionEmail,
  submissionEntityName,
  submissionPropertyAddress,
} from "../submissionHelpers";

/**
 * Confirmation for "create a new loan from this submission".
 *
 * It is ONLY a confirmation, deliberately: POST
 * /underwriting/create_deal_from_submission/{createParam}/{id} takes no body and
 * does the entire job server-side — it builds the loan off the submission,
 * copies the borrower and subject properties onto it, sets `originator` from the
 * caller's company, adds the caller as a permitted user, carries the uploaded
 * files across (sha1-deduped), and stamps the submission with the new dealId and
 * loanNumber. There is no field for the user to fill in, so inventing a form
 * here would just be a second place for loan data to diverge from the
 * submission.
 *
 * What it DOES owe the user is a clear statement of what is about to happen —
 * this creates a real loan and is not undoable from this screen.
 */
const CreateLoanDialog = ({ open, row, saving, onClose, onConfirm }) => {
  const docCount = row ? submissionDocumentCount(row) : 0;

  // Everything below is what the backend copies onto the new loan. Listed so the
  // confirmation says what will actually be carried, not just "are you sure?".
  const carried = row
    ? [
        ["Borrower", submissionBorrowerName(row) || "Unnamed borrower"],
        ["Email", submissionEmail(row)],
        ["Entity", submissionEntityName(row)],
        ["Subject property", submissionPropertyAddress(row)],
        [
          "Loan amount",
          row.baseLoanAmount != null ? formatDollar(row.baseLoanAmount) : null,
        ],
        [
          "Documents",
          docCount ? `${docCount} file${docCount > 1 ? "s" : ""}` : null,
        ],
      ].filter(([, v]) => v)
    : [];

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle sx={{ p: "20px 22px 0" }}>
        <Flx between centerY gap={2}>
          <Box>
            <Txt
              component="div"
              sx={{ fontSize: 17, fontWeight: 700, color: colors.slate900 }}
            >
              Create a new loan
            </Txt>
            {row && (
              <Txt
                component="div"
                sx={{ fontSize: 13, color: colors.slate500, mt: "4px" }}
              >
                From this {submissionTypeLong(row).toLowerCase()}
              </Txt>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            disabled={saving}
            size="small"
            aria-label="Close"
            sx={{ color: colors.slate400, mt: "-4px", mr: "-6px" }}
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Flx>
      </DialogTitle>

      <DialogContent sx={{ p: "16px 22px 0" }}>
        <Box
          sx={{
            border: `1px solid ${colors.slate200}`,
            borderRadius: "10px",
            p: "12px 14px",
            background: colors.slate50,
          }}
        >
          {carried.map(([label, value]) => (
            <Flx
              key={label}
              between
              gap={2}
              sx={{ py: "5px", fontSize: 12.5, minWidth: 0 }}
            >
              <Box sx={{ color: colors.slate500, flexShrink: 0 }}>{label}</Box>
              <Box
                sx={{
                  fontWeight: 550,
                  textAlign: "right",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {value}
              </Box>
            </Flx>
          ))}
        </Box>

        <Txt
          component="div"
          sx={{ fontSize: 12.5, color: colors.slate500, mt: "14px" }}
        >
          A new loan will be opened with this borrower, their subject properties
          and their uploaded documents carried across. You'll be added to it, and
          this submission will be linked to the loan.
        </Txt>
      </DialogContent>

      <DialogActions sx={{ p: "18px 22px 20px" }}>
        <Button
          onClick={onClose}
          variant="text"
          disabled={saving}
          sx={PANEL_BTN_OUTLINE_SX}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={saving}
          startIcon={!saving && <CheckRoundedIcon sx={{ fontSize: 17 }} />}
          onClick={onConfirm}
          sx={PANEL_BTN_SX}
        >
          {saving ? "Creating…" : "Create loan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLoanDialog;
